import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../service/user.service';
import { PlanthunterService } from '../../service/planthunter.service';
import { Planthunter } from '../../classes/planthunter';
import { ShopHelper } from '../../helper/shop.helper';
import { ApplicationService } from '../../service/application.service';
import { LocationStrategy } from '@angular/common';
import { WindowRef } from '../../service/window-ref.service';
import { NotfoundComponent } from '../notfound/notfound.component';
import { LazyLoadDirective } from '../lazyload/ng-lazy-load.directive';
import { ProductItemGridComponent } from '../products/productitem.grid.component';

// Amount of products to get for a planthunter
const AMOUNT_OF_PRODUCTS = 99;

@Component({
    selector: 'planthunter-detail-component',
    templateUrl: './planthunter.detail.component.html',
    styleUrls: ['planthunter.component.scss'],
    standalone: true,
    imports: [ProductItemGridComponent, LazyLoadDirective, NotfoundComponent]
})
export class PlanthunterDetailComponent implements OnInit {
  private planthunterService = inject(PlanthunterService);
  private locationStrategy = inject(LocationStrategy);
  protected router = inject(Router);

  private static winRef: WindowRef = new WindowRef();
  readonly loading = signal<boolean>(true);
  @Input() hunterId: string;
  @Input() planthunterOverviewURL: string;
  readonly planthunter = signal<Planthunter>(undefined);
  readonly show404 = signal<boolean>(false);
  private applicationService: ApplicationService;
  private userService: UserService;

  constructor() {
    const applicationService = inject(ApplicationService);
    const userService = inject(UserService);

    this.applicationService = applicationService;
    this.userService = userService;
  }

  ngOnInit(): void {
    this.getPlanthunterIfUserIsLoggedIn();
  }

  isUserLoggedin(): boolean {
    return this.userService.isLoggedIn();
  }

  viewProductDetail(detailUrl) {
    ShopHelper.goToWebshopUrl(detailUrl + ';origin=planthunter');
  }

  followPlanthunter(): void {
    this.planthunterService
      .followPlanthunter(this.planthunter().code)
      .then(() => this.planthunter.update(hunter => ({...hunter, volgStatus: true})));
  }

  unfollowPlanthunter(): void {
    this.planthunterService
      .unfollowPlanthunter(this.planthunter().code)
      .then(() => this.planthunter.update(hunter => ({...hunter, volgStatus: false})));
  }

  /**
   * Decides if the link to the Hunters Overview page should be done by the Angular Routers (webshop) or
   * by setting the location and pushing the state (Website).
   *
   * This way the hunters on the KvK shop do not trigger a full page refresh since the whole shop is a
   * Angular application. This is not the case for the website.
   */
  gotoHunterOverview(): void {
    if (this.applicationService.isBrowsingWebsite(window.location.href)) {
      this.locationStrategy.pushState(
        { backToOverview: true },
        'Hunters Overview',
        this.planthunterOverviewURL,
        ''
      );
      PlanthunterDetailComponent.winRef.nativeWindow.location.href =
        this.planthunterOverviewURL;
      return;
    }

    this.router.navigate([this.planthunterOverviewURL]);
  }

  private getPlanthunterIfUserIsLoggedIn() {
    if (this.isUserLoggedin()) {
      this.planthunterService
        .getSinglePlanthunter(this.hunterId, AMOUNT_OF_PRODUCTS)
        .then((planthunter) => {
          this.planthunter.set(planthunter);
          this.loading.set(false);
        })
        .catch((error) => {
          this.loading.set(false);
          this.show404.set(true);
        });

      return;
    }

    const planthunterDetailUrl =
      PlanthunterDetailComponent.winRef.nativeWindow.location.href;
    PlanthunterDetailComponent.winRef.nativeWindow.location.href =
      this.applicationService.getCmsLoginUrl(planthunterDetailUrl);
  }
}
